import { useSignal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import {
  type BillingInfo,
  CREDITS_PER_SEAT,
  PACK_MONTHS,
  PACKS,
  type Pack,
  packCredits,
  SEAT_PRICE_CENTS,
} from "~/protocol/billing";
import { buyPack, getBilling, openPortal, startCheckout } from "~/web/src/api";
import { errorText, OFFLINE } from "~/web/src/errors";
import { TopUpForm } from "~/web/src/pages/billing-topup";
import { TOP_UP_FAILED, topUpSummary } from "~/web/src/pages/topup-copy";
import { Topbar } from "~/web/src/topbar";

// /billing: the org's plan, members, credits, the lots they come from and whether top-up is on for
// every member; the billed seats, the next invoice, Subscribe, credit packs, the top-up setting and
// Manage billing (Stripe's portal) for org admins. Stripe sends the browser
// back here with `?session=<id>`; the server reads that session from Stripe, and while its payment
// is still being confirmed the page asks again every 2 s, for up to 2 minutes (a failed ask
// included), then drops the session from the URL once it has landed. Back from the portal
// (`?from=portal`) the server re-reads the subscription once, and the page drops the marker from
// the URL.

const SEAT_DOLLARS = SEAT_PRICE_CENTS / 100;
const POLL_MS = 2_000;
const POLL_FOR_MS = 120_000;

const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

const longDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { dateStyle: "long" });

/** One live lot, as a whole line per kind and number. */
const LOT: Record<BillingInfo["lots"][number]["kind"], (n: number, date: string) => string> = {
  seat: (n, date) =>
    n === 1 ? `1 seat credit, expires ${date}` : `${n} seat credits, expire ${date}`,
  purchased: (n, date) =>
    n === 1 ? `1 pack credit, expires ${date}` : `${n} pack credits, expire ${date}`,
  grant: (n, date) =>
    n === 1
      ? `1 credit from Featurette, expires ${date}`
      : `${n} credits from Featurette, expire ${date}`,
};

const STATUS: Record<BillingInfo["state"], string> = {
  subscribed: "Subscribed to Pro",
  registered: "Not subscribed",
  confirming: "Confirming your payment…",
};

export function BillingPage() {
  const info = useSignal<BillingInfo | null>(null);
  const error = useSignal<string | null>(null);
  const busy = useSignal(false);
  /** Which button is taking the browser to Stripe, for its busy label. */
  const opening = useSignal<Pack | "subscribe" | "portal" | null>(null);
  const slow = useSignal(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const session = params.get("session") ?? undefined;
    // Back from the portal by the Back button, reloaded rather than restored: no marker in the URL,
    // so the way here says it.
    const backAgain =
      (performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined)
        ?.type === "back_forward";
    const fromPortal = params.get("from") === "portal" || backAgain;
    const deadline = Date.now() + POLL_FOR_MS;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let live = true;
    // Back from Stripe's page with the browser's Back button: the page may come back as it was
    // left, with the button still busy and the state from before the admin changed anything.
    const restored = (e: PageTransitionEvent) => {
      if (!e.persisted) return;
      busy.value = false;
      opening.value = null;
      getBilling(undefined, true).then(
        (next) => {
          info.value = next;
        },
        () => {},
      );
    };
    window.addEventListener("pageshow", restored);
    const again = () => {
      if (Date.now() < deadline) timer = setTimeout(() => void load(), POLL_MS);
      else if (info.value) slow.value = true;
      else error.value = "Couldn't load billing. Reload this page in a few minutes.";
    };
    const load = async () => {
      try {
        const next = await getBilling(session, fromPortal && !info.value);
        if (!live) return;
        info.value = next;
        error.value = null;
        if (next.state !== "confirming") {
          if (session || fromPortal) history.replaceState(null, "", "/billing");
        } else {
          again();
        }
      } catch (err) {
        if (!live) return;
        // While a payment is being confirmed a failed ask is one more ask, not the end of it.
        if (info.value?.state === "confirming" || (session && !info.value)) again();
        else
          error.value =
            err instanceof TypeError ? OFFLINE : `Couldn't load billing: ${errorText(err)}`;
      }
    };
    void load();
    return () => {
      live = false;
      clearTimeout(timer);
      window.removeEventListener("pageshow", restored);
    };
  }, [info, error, slow, busy, opening]);

  /** Off to Stripe: Checkout or the portal. */
  async function leave(
    open: () => Promise<{ url: string }>,
    what: "checkout" | "the billing portal",
    which: NonNullable<typeof opening.value>,
  ) {
    // The buttons stay focusable while busy (aria-disabled): a disabled one drops focus to the page.
    if (busy.value) return;
    busy.value = true;
    opening.value = which;
    error.value = null;
    try {
      location.assign((await open()).url);
    } catch (err) {
      // The server's refusals are sentences; anything else is a bare status.
      const said = errorText(err);
      error.value =
        err instanceof TypeError
          ? OFFLINE
          : /^\d{3} /.test(said)
            ? `Couldn't open ${what}. Try again in a minute.`
            : `Couldn't open ${what}: ${said}`;
      busy.value = false;
      opening.value = null;
      // A refusal means the org moved on (someone subscribed, a role changed): show where it is now.
      info.value = await getBilling().catch(() => info.value);
    }
  }

  const b = info.value;
  return (
    <div class="app">
      <Topbar name="Billing" />
      <main class="home">
        <section class="billing">
          <h1>Billing</h1>
          {!b && !error.value && <span class="dim">loading…</span>}
          {b && (
            <>
              <div class="now" aria-live="polite">
                <p class="status">{STATUS[b.state]}</p>
                {b.endsAt && (
                  <p class="dim">
                    Pro ends on {longDate(b.endsAt)}. Seat credits expire then. Other credits, such
                    as those from credit packs, stay usable until they expire.
                    {b.isAdmin &&
                      ' To keep Pro, choose Manage billing, then "Don\'t cancel subscription".'}
                  </p>
                )}
                {b.paymentRetrying && b.isAdmin && (
                  <p class="warn">
                    Your last payment didn't go through, and Stripe is retrying it. This month's
                    seat credits arrive once it's paid, and Pro ends if every retry fails. To change
                    the card Stripe charges, choose Manage billing.
                  </p>
                )}
                {b.topUpFailed && b.isAdmin && <p class="warn">{TOP_UP_FAILED}</p>}
                {b.state === "confirming" && (
                  <p class="dim">
                    {slow.value
                      ? "This is taking longer than usual. Reload this page in a few minutes."
                      : "This page updates as soon as Stripe confirms it."}
                  </p>
                )}
              </div>
              <dl>
                <div>
                  <dt>Members now</dt>
                  <dd>
                    {count(b.members, "member", "members")}
                    <span class="dim">Every verified member of your org is a seat.</span>
                  </dd>
                </div>
                {b.billedSeats !== null ? (
                  <div>
                    <dt>Billed seats</dt>
                    <dd>
                      {count(b.billedSeats, "seat", "seats")}
                      <span class="dim">
                        {b.nextInvoiceCents !== null
                          ? `Your next invoice is $${b.nextInvoiceCents / 100}, plus tax. When members join or leave, your invoices follow, with nothing charged on the spot.`
                          : "Pro is ending. An invoice before then may cover only part of a month."}
                      </span>
                    </dd>
                  </div>
                ) : (
                  b.seatsUnknown && (
                    <div>
                      <dt>Billed seats</dt>
                      <dd>
                        <span class="dim">
                          Couldn't check your billed seats just now. Billing isn't affected. Reload
                          in a minute to see them.
                        </span>
                      </dd>
                    </div>
                  )
                )}
                <div>
                  <dt>Credits</dt>
                  <dd>
                    {count(b.credits, "credit", "credits")}
                    <span class="dim">One credit removes the watermark from one video.</span>
                    {b.lots.length > 0 && (
                      // A list styled without markers: Safari drops its semantics unless told.
                      // biome-ignore lint/a11y/noRedundantRoles: see above
                      <ul class="lots dim" role="list">
                        {b.lots.map((l) => (
                          <li key={l.id}>{LOT[l.kind](l.remaining, longDate(l.expiresAt))}</li>
                        ))}
                      </ul>
                    )}
                  </dd>
                </div>
                {(b.canBuyPacks || b.topUp) && (
                  <div>
                    <dt>Top-up</dt>
                    <dd>{topUpSummary(b)}</dd>
                  </div>
                )}
              </dl>
              {/* Above every button, so a refused click's reason is next to where it was made. */}
              {error.value && (
                <div class="error" role="alert">
                  {error.value}
                </div>
              )}
              {b.canBuyPacks &&
                b.state !== "confirming" &&
                (b.isAdmin ? (
                  <div class="packs">
                    <p class="dim">
                      Need more? Buy a credit pack. Pack prices are plus tax. Pack credits last{" "}
                      {PACK_MONTHS} months from purchase and stay usable if Pro ends.
                    </p>
                    <div class="pack-buttons">
                      {PACKS.map((pack) => (
                        // Equal buttons whatever their words, so none wraps alone or moves when one
                        // turns busy. Their name is the two lines as shown ("$10 20 credits").
                        <button
                          key={pack}
                          type="button"
                          class="btn pack"
                          aria-disabled={busy.value}
                          onClick={() => void leave(() => buyPack(pack), "checkout", pack)}
                        >
                          <strong>${pack}</strong>
                          <span>
                            {opening.value === pack
                              ? "Opening checkout…"
                              : `${packCredits(pack)} credits`}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null)}
              {b.canBuyPacks && b.state !== "confirming" && b.isAdmin && (
                <TopUpForm
                  b={b}
                  onSaved={async () => {
                    info.value = await getBilling();
                  }}
                />
              )}
              {b.state === "registered" &&
                (!b.canSubscribe ? (
                  <p class="dim">
                    Your org already has a subscription, but it isn't active.
                    {b.isAdmin && b.canManage && " Manage billing shows it in Stripe."}
                  </p>
                ) : b.isAdmin ? (
                  <div class="subscribe">
                    <p class="dim">
                      Pro is ${SEAT_DOLLARS} a month per seat, plus tax. Each paid month adds{" "}
                      {CREDITS_PER_SEAT} credits per seat, shared by your org. Unused seat credits
                      expire at the end of each billing month.
                    </p>
                    <button
                      type="button"
                      class="btn"
                      aria-disabled={busy.value}
                      onClick={() => void leave(startCheckout, "checkout", "subscribe")}
                    >
                      {opening.value === "subscribe"
                        ? "Opening checkout…"
                        : `Subscribe for $${SEAT_DOLLARS * Math.max(1, b.members)} a month`}
                    </button>
                  </div>
                ) : (
                  <p class="dim">Only an org admin can subscribe your org.</p>
                ))}
              {b.canManage &&
                b.state !== "confirming" &&
                (b.isAdmin ? (
                  <div class="manage">
                    <button
                      type="button"
                      class="btn"
                      aria-disabled={busy.value}
                      onClick={() => void leave(openPortal, "the billing portal", "portal")}
                    >
                      {opening.value === "portal"
                        ? "Opening the billing portal…"
                        : "Manage billing"}
                    </button>
                  </div>
                ) : (
                  <p class="dim">
                    {b.canBuyPacks
                      ? "Only an org admin can buy credits, set up top-up or manage your org's billing."
                      : "Only an org admin can manage your org's billing."}
                  </p>
                ))}
            </>
          )}
          {!b && error.value && (
            <div class="error" role="alert">
              {error.value}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
